'use client';

import { useState, useEffect, useCallback } from 'react';
import { Loader2, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import axiosInstance from '@/lib/axios';
import type { Community } from '@/types';
import { useDebounce } from '@/hooks/use-debounce-custom';

interface ShareProjectModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  communityId: string;
  projectId: string;
  onSuccess: () => void;
}

export function ShareProjectModal({
  open,
  onOpenChange,
  communityId,
  projectId,
  onSuccess,
}: ShareProjectModalProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [isFetchingOptions, setIsFetchingOptions] = useState(false);
  const [communities, setCommunities] = useState<Community[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCommunity, setSelectedCommunity] = useState<Community | null>(null);

  const debouncedSearch = useDebounce(searchQuery, 300);

  const fetchCommunities = useCallback(
    async (search: string) => {
      setIsFetchingOptions(true);
      try {
        const trimmed = search.trim();
        const res = await axiosInstance.get(
          `/api/communities/${communityId}/projects/${projectId}/share/options`,
          { params: trimmed ? { search: trimmed } : {} }
        );
        setCommunities(res.data);
      } catch (error) {
        console.error('Failed to fetch communities', error);
        toast.error('Failed to load communities');
      } finally {
        setIsFetchingOptions(false);
      }
    },
    [communityId, projectId]
  );

  useEffect(() => {
    if (open) {
      fetchCommunities(debouncedSearch);
    }
  }, [open, debouncedSearch, fetchCommunities]);

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      setSearchQuery('');
      setSelectedCommunity(null);
      setCommunities([]);
    }
    onOpenChange(isOpen);
  };

  const handleShare = async () => {
    if (!selectedCommunity) return;

    setIsLoading(true);
    try {
      await axiosInstance.post(`/api/communities/${communityId}/projects/${projectId}/share`, {
        communityId: selectedCommunity.id,
      });
      toast.success(`Project shared with ${selectedCommunity.name}`);
      handleOpenChange(false);
      onSuccess();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: string } } };
      toast.error(err.response?.data?.error || 'Failed to share project');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Share Project</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search communities..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>

          <div className="max-h-[300px] overflow-y-auto space-y-2 border rounded-md p-2">
            {isFetchingOptions ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : communities.length === 0 ? (
              <p className="text-center text-muted-foreground py-4">
                No communities available to share with
              </p>
            ) : (
              communities.map((community) => (
                <div
                  key={community.id}
                  onClick={() => setSelectedCommunity(community)}
                  className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                    selectedCommunity?.id === community.id
                      ? 'bg-primary/10 border border-primary'
                      : 'hover:bg-muted'
                  }`}
                >
                  {community.logo ? (
                    <img
                      src={community.logo}
                      alt={community.name}
                      className="h-10 w-10 rounded-full object-cover"
                    />
                  ) : (
                    <div className="h-10 w-10 rounded-full bg-primary/20 flex items-center justify-center">
                      <span className="text-sm font-medium">
                        {community.name.charAt(0).toUpperCase()}
                      </span>
                    </div>
                  )}
                  <span className="font-medium">{community.name}</span>
                </div>
              ))
            )}
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={() => handleOpenChange(false)}>
              Cancel
            </Button>
            <Button onClick={handleShare} disabled={!selectedCommunity || isLoading}>
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Sharing...
                </>
              ) : (
                'Share'
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
